<template>
    <v-container container--fluid>
        <v-layout row wrap align-center justify-space-around class="pa-2 mx-auto">
            <v-flex xs12 class="text-left">
                <v-btn text color="warning" :to="{ name: 'invoiceDetail', params: { invoice_id: invoiceId } }" class="ma-0 mb-2"
                    ><v-icon left>arrow_back</v-icon> Invoice #{{ invoiceId }}</v-btn
                >

                <h1 class="grey--text lighten-3 mb-2">Invoice #{{ invoiceId }}</h1>

                <v-overlay :absolute="true" :value="overlay" z-index="99" opacity="0.56">
                    <v-card>
                        <v-card-text v-html="overlayText"></v-card-text>
                        <v-card-actions>
                            <v-btn medium block dark :color="overlayColor" @click="overlay = false">Close <v-icon medium right>highlight_off</v-icon> </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-overlay>

                <div class="mt-4" v-if="invoiceErrors.any()" v-cloak>
                    <v-alert dismissible v-model="showInvError" outlined transition="scale-transition" type="warning">{{ invoiceErrors.errors }}</v-alert>
                </div>

                <div v-if="invoiceLoaded && viewInvoice" v-cloak>
                    <v-stepper v-model="invStep" vertical>
                        <v-stepper-step :complete="invStep > 1" step="1" :editable="invStep > 1">
                            How much would you like to pay?
                            <small class="caption warning--text font-weight-bold">Amount includes Active & Creative Kids vouchers.</small>
                        </v-stepper-step>

                        <v-stepper-content step="1">
                            <v-card color="grey lighten-5" class="mb-5 elevetation-6">
                                <div class="Ftable">
                                    <div class="Ftable__row Ftable__header">
                                        <div class="Ftable__item cols-2 text-left">Invoice</div>
                                        <div class="Ftable__item text-right"></div>
                                        <div class="Ftable__item text-right">Price</div>
                                    </div>
                                    <div class="Ftable__row">
                                        <div class="Ftable__item cols-2 text-left">
                                            <span class="d-block pt-1"
                                                >Invoice #{{ invoiceDetail.inv_id }} - Owing
                                                {{ owingPayments(invoiceDetail.inv_amount, invoiceDetail.inv_payments) | currency }}</span
                                            ><br />
                                        </div>
                                        <div class="Ftable__item text-right"></div>
                                        <div class="Ftable__item text-right" id="priceField">
                                            <p class="subtitle-1 font-weight-bold primary--text text-left mt-1 ml-1 mb-0">Enter Amount to Pay</p>
                                            <v-text-field
                                                :disabled="couponApplied"
                                                outlined
                                                class="font-weight-bold grey lighten-4"
                                                prepend-icon="attach_money"
                                                label=""
                                                v-model="price"
                                            ></v-text-field>
                                        </div>
                                    </div>
                                    <div class="Ftable__row">
                                        <div class="Ftable__item cols-2 no-border right-border top-border"></div>
                                        <div class="Ftable__item text-right">
                                            <span><strong>Subtotal</strong></span>
                                        </div>
                                        <div class="Ftable__item text-right">
                                            <span>{{ subTotal | currency("$") }}</span>
                                        </div>
                                    </div>
                                    <div class="Ftable__row">
                                        <div class="Ftable__item cols-2 no-border right-border bottom-border"></div>
                                        <div class="Ftable__item text-right">
                                            <span><strong>Tax</strong></span>
                                        </div>
                                        <div class="Ftable__item text-right">
                                            <span>{{ taxTotal | currency("$") }}</span>
                                        </div>
                                    </div>
                                    <div class="Ftable__row">
                                        <div class="Ftable__item cols-2"></div>
                                        <div class="Ftable__item text-right">
                                            <span>
                                                <strong>Active & Creative Kids / {{ appNameUrls.client }} Voucher</strong>
                                            </span>
                                            <v-btn v-if="couponApplied" small dark color="red" @click="removeVoucher">remove</v-btn>
                                        </div>
                                        <div class="Ftable__item text-right">
                                            <span>{{ discountTotal | currency("$") }}</span>
                                        </div>
                                    </div>
                                    <div class="Ftable__row">
                                        <div class="Ftable__item cols-2 no-border right-border top-border"></div>
                                        <div class="Ftable__item text-right">
                                            <span><strong>Total</strong></span>
                                        </div>
                                        <div class="Ftable__item text-right">
                                            <span>{{ grandTotal | currency("$") }}</span>
                                        </div>
                                    </div>
                                </div>
                            </v-card>

                            <v-btn v-if="!couponApplied" :disabled="price < 1" color="primary" @click="invStep = 2">Continue</v-btn>
                            <v-btn v-if="couponApplied" color="primary" @click="invStep = 3">Continue</v-btn>
                        </v-stepper-content>

                        <v-stepper-step
                            :rules="[() => !couponErrors.any()]"
                            :complete="invStep > 2 || couponApplied"
                            step="2"
                            :color="(couponApplied || invStep > 2) ? 'success' : 'primary'"
                            :editable="invStep > 1"
                        >
                            Enter Active & Creative Kids / {{ appNameUrls.client }} Voucher
                            <small>{{ getCouponMessage }}</small>
                        </v-stepper-step>

                        <v-stepper-content step="2">
                            <v-card outlined color="grey lighten-5" class="mb-5 elevetation-6">
                                <v-expansion-panels :value="0">
                                    <v-expansion-panel class="secondary white--text">
                                        <v-expansion-panel-header>Active & Creative Kids / {{ appNameUrls.client }} Voucher</v-expansion-panel-header>
                                        <v-expansion-panel-content>
                                            <v-card>
                                                <v-card-text>
                                                    <coupon :grand-total="subTotal" @submitted-voucher="submittedVoucher"></coupon>
                                                </v-card-text>
                                            </v-card>
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </v-card>

                            <v-btn color="primary" @click="invStep = 3">Continue</v-btn>
                        </v-stepper-content>

                        <v-stepper-step :rules="[() => !paymentErrors.any()]" :complete="invStep > 3" step="3" :editable="invStep > 2">
                            Make Online Payment
                            <small
                                v-if="paymentErrors.any() && !paymentErrors.has('error_response')"
                                class="caption font-weight-bold secondary--text"
                                v-html="getPaymentMessage"
                            ></small>
                            <small
                                v-if="paymentErrors.any() && paymentErrors.has('error_response')"
                                class="caption font-weight-bold secondary--text"
                                v-html="paymentErrors.get('error_response')"
                            ></small>
                        </v-stepper-step>

                        <v-stepper-content step="3">
                            <!-- <donation v-if="showCpDonation"></donation> -->

                            <v-card color="grey lighten-5" class="mb-5 elevetation-6">
                                <v-card-text>
                                    <component :is="ftGateway" v-if="grandTotal > 0" @submitted-pay="submitPay"></component>
                                    <v-alert dense outlined v-if="grandTotal <= 0" type="info">No Payment required. Click button below to complete process.</v-alert>
                                </v-card-text>
                            </v-card>

                            <v-btn :loading="payLoading" :disabled="!validPaymentForm" color="primary" @click="payInvoice" class="mr-2 text-left"
                                ><v-icon left>local_atm</v-icon>{{ payHtml }}: {{ grandTotal | currency }}</v-btn
                            >
                            <v-btn v-if="grandTotal > 0" @click="clearPay" color="warning" :disabled="payLoading" class="text-left"
                                >clear
                                <v-icon right>clear</v-icon>
                            </v-btn>
                        </v-stepper-content>
                    </v-stepper>
                </div>

                <div class="justify-center align-center layout" v-if="!invoiceLoaded || !viewInvoice"><v-icon left medium>warning</v-icon> No Invoice Data</div>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>
import store from "./_store";

import { EventBus } from "@shared/EventBus.js";
import Eway from "@components/gateways/Eway.vue";
import MerchantWarrior from "@components/gateways/MerchantWarrior.vue";
import Coupon from "@components/coupons/Coupon.vue";
import Donation from "@components/donation/Donation.vue";

export default {
    name: "InvoicePaymentModule",
    components: {
        eway: Eway,
        merchantWarrior: MerchantWarrior,
        coupon: Coupon,
        donation: Donation,
    },
    props: {
        "invoice-id": {
            type: [Number, String],
            required: true,
        },
    },
    data() {
        return {
            invStep: 1,
            editablePanel: false,

            validPaymentForm: true,
            price: 0.0,
            showInvError: false,

            invoiceDetail: {},
            invoiceLoaded: false,
            viewInvoice: true,

            overlay: false,
            timeout: 500,
            overlayText: "",
            overlayColor: "cyan darken-2",

            // ftGateway: process.env.MIX_FT_GATEWAY,
            ftGateway: gtwPw,
            showCpDonation: showDonation,
        };
    },
    computed: {
        donationTotal() {
            return this.$store.getters["donation/donationTotal"];
        },
        discountTotal() {
            return this.$store.getters["coupon/discountTotal"];
        },
        couponApplied() {
            return this.$store.getters["coupon/couponApplied"];
        },
        couponErrors() {
            return this.$store.getters["coupon/couponErrors"];
        },
        couponLoading() {
            return this.$store.getters["coupon/couponStatus"] == "loading";
        },
        getCouponMessage() {
            return this.$store.getters["coupon/couponMessage"];
        },
        invoices() {
            return this.$store.getters["invoice/invoices"];
        },
        invoice() {
            return this.$store.getters["invoice/invoiceDetailById"](this.invoiceId);
        },
        invoiceErrors() {
            return this.$store.getters["invoice/invoiceErrors"];
        },
        paymentErrors() {
            return this.$store.getters["invoice/paymentErrors"];
        },
        getPaymentMessage() {
            if (this.paymentErrors.any()) {
                if (this.paymentErrors.has("error_auth")) {
                    return this.paymentErrors.get("error_auth");
                }

                if (this.paymentErrors.has("error_paid")) {
                    return this.paymentErrors.get("error_paid");
                }

                if (this.paymentErrors.has("error_response")) {
                    return this.paymentErrors.get("error_response");
                }

                if (this.paymentErrors.has("error_validation")) {
                    return "Invoice Data Invalid";
                }

                if (this.paymentErrors.has("error_payment")) {
                    return this.paymentErrors.get("error_payment");
                }

                return "Payment Invalid";
            }

            return "";
        },
        payLoading() {
            return this.$store.getters["invoice/paymentStatus"] == "loading";
        },
        payHtml() {
            if (this.grandTotal > 0) {
                return "Pay Invoice";
            }

            return "Complete Invoice";
        },
        user() {
            return this.$store.getters["user/getProfile"];
        },
        invoiceCart() {
            return this.$store.getters["invoice/invoiceCart"];
        },
        subTotal() {
            return this.invoiceCart.p_price;
        },
        taxTotal() {
            return this.subTotal / (taxRate + 1);
        },
        grandTotal() {
            return this.subTotal - this.discountTotal + this.donationTotal;
        },
    },
    watch: {
        price() {
            let owing = this.owingPayments(this.invoiceDetail.inv_amount, this.invoiceDetail.inv_payments);

            if (isNaN(this.price)) {
                this.price = 0;
            }

            if (parseFloat(this.price) > parseFloat(owing)) {
                this.showInvError = true;
                this.$store.dispatch("invoice/invoiceErrors", { error: "Amount to pay cannot be greater than the amount owing" });
                this.price = 0;
            } else {
                // this.showInvError = false;
                let nameDetail = "Invoice #" + this.invoiceDetail.inv_id;
                this.$store.dispatch("invoice/updateInvoiceCart", { name: nameDetail, price: this.price });
            }
        },
        invoices(n, o) {
            let invId = this.invoiceId;
            this.invoiceDetail = _.find(n, function(inv) {
                return inv.inv_id == invId;
            });

            this.invoiceLoaded = true;
            if (_.isEmpty(this.invoiceDetail) && this.invoice === undefined) {
                this.invoiceLoaded = false;
                this.viewInvoice = false;
            }
        },
        invoice() {
            this.invoiceDetail = this.invoice;

            if (!_.isEmpty(this.invoiceDetail)) {
                this.invoiceLoaded = true;
            }
        },
    },
    methods: {
        submittedVoucher() {
            this.invStep = 1;
        },
        removeVoucher() {
            this.$store.dispatch("coupon/removeVoucher");
        },
        clearVoucher() {
            this.$store.dispatch("coupon/clearCouponErrors");
        },
        total(payments) {
            let sum = parseFloat(0.0);
            return payments.reduce(function(sum, payment) {
                let discount = !_.isNil(payment.p_discount) ? payment.p_discount : 0.0;
                let amount = payment.p_amount;

                if (payment.p_refunded) {
                    amount = payment.p_amount * -1;
                }

                return sum + parseFloat(amount) + parseFloat(discount);
            }, 0.0);
        },
        owingPayments(total, payments) {
            let sum = !_.isEmpty(payments) ? this.total(payments) : 0;
            let owing = parseFloat(parseFloat(total) - parseFloat(sum)).toFixed(2);
            return owing;
        },

        clearPay() {
            this.alert = false;
            EventBus.$emit("clear-pay");
            this.$store.dispatch("invoice/clearPaymentErrors");
        },
        payInvoice: function() {
            if (this.grandTotal > 0) {
                EventBus.$emit("submit-pay");
            } else {
                this.submitPay();
            }
        },
        submitPay: function(payload) {
            this.validPaymentForm = false;

            const invoice = this.invoiceId;
            const cardPayload = payload;
            const couponCart = this.$store.getters["coupon/couponCart"];
            const donationCart = this.$store.getters["donation/donationCart"];

            this.$store
                .dispatch("invoice/payInvoice", {
                    invoice: invoice,
                    cardPayload: cardPayload,
                    couponCart: couponCart,
                    donationCart: donationCart,
                })
                .then(order => {
                    this.overlayText = "Payment was successful.";
                    this.overlay = true;
                    this.overlayColor = "primary";

                    this.clearVoucher();

                    setTimeout(() => {
                        this.$store
                            .dispatch("invoice/invoiceRequest")
                            .then(() => {
                                if (order.o_paid) {
                                    this.$router.push({ name: "orderDetail", params: { order_id: order.o_id } });
                                } else {
                                    this.$router.push({ name: "invoiceDetail", params: { invoice_id: this.invoiceId } });
                                }
                            })
                            .catch();
                    }, this.timeout / 4);
                })
                .catch(err => {
                    this.validPaymentForm = true;
                    // console.log(err);

                    setTimeout(() => {
                        EventBus.$emit("clear-pay");
                    }, 500);
                });
        },
        resetInvoice: function() {
            if (!_.isEmpty(this.invoices)) {
                this.$store.dispatch("invoice/updateInvoices", this.invoices);

                let invId = this.invoiceId;
                this.invoiceDetail = _.find(this.invoices, function(inv) {
                    return inv.inv_id == invId;
                });

                if (!_.isEmpty(this.invoiceDetail)) {
                    this.invoiceLoaded = true;
                }
            }
        },
    },
    beforeCreate() {
        // this.$store.dispatch('user/userRequest');
        if (_.isEmpty(this.$store.getters["invoice/invoices"])) {
            this.$store.dispatch("invoice/invoiceRequest").then(() => {
                let nameDetail = "Invoice #" + this.invoiceDetail.inv_id;
                this.$store.dispatch("invoice/updateInvoiceCart", { name: nameDetail, price: 0 });
            });
        }
    },
    created() {
        this.resetInvoice();
    },
    mounted() {
        this.$store.dispatch("invoice/resetTotals");
        if (_.isEmpty(this.invoiceDetail) && this.invoice !== undefined) {
            this.invoiceDetail = this.invoice;
        }

        this.invoiceLoaded = true;
    },
};
</script>

<style lang="scss">
[v-cloak] {
    display: none;
}
[v-cloak] > * {
    display: none;
}

.v-stepper__step.v-stepper__step--error .v-stepper__label,
.theme--light.v-stepper .v-stepper__step--active.v-stepper__step--error .v-stepper__label {
    text-shadow: none !important;
}

#priceField .v-input__slot,
#priceField .v-input__slot input[type="text"] {
    background: #f8c688 !important;
}

.Ftable {
    $light-color: #ffffff;
    $header-color: #f8c689;
    $dark-color: #f2f2f2;

    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    /*border: 1px solid $dark-color;*/
    font-size: 1rem;
    padding: 0;
    margin: 0;
    line-height: 1.5;

    .Ftable__row {
        width: 100%;
        display: flex;

        &.Ftable__header {
            font-weight: bold;

            .Ftable__item {
                background: $header-color;
            }
        }

        .Ftable__item {
            flex: 1 1 25%;
            word-break: break-word;
            padding: 0.25em;
            border: 1px solid $light-color;
            background: $dark-color;

            &.cols-2 {
                flex: 1 1 50%;
            }

            &.no-border {
                border: 0px solid $light-color;
            }

            &.right-border {
                border-right: 1px solid $light-color;
            }

            &.top-border {
                border-top: 1px solid $light-color;
            }

            &.bottom-border {
                border-bottom: 1px solid $light-color;
            }
        }
    }
}
</style>
